import { Injectable } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { smallScreenMaxWidthBase } from 'environments/environment-helpers';
import { getPageName, ROOT_PAGE_NAMES } from 'app/app-page-helpers';
import { BottomNavItem } from 'components/cs-bottom-nav/cs-bottom-nav';
import type { HomePage } from 'pages/home/home';
import { HomeTab, IHomePage } from 'pages/home/home-interface';
import { ITrainingPage } from 'pages/training/training-interface';
import { INutritionPage } from 'pages/nutrition/nutrition-interface';
import { ISettingsPage } from 'pages/settings-page/settings-page-interface';
import { IAdminImpersonatedUserPage } from 'pages/admin-impersonated-user/admin-impersonated-user-interface';
import { MenuController } from 'providers/menu-controller';
import { NavController } from 'providers/nav-controller';
import { OverlayStackService } from 'providers/overlay-stack.service';
import { ActivityMenuService } from 'services/activity-menu.service';
import { getSideMenuCollapsedWidth } from 'services/browser-utils';
import { DebugService } from 'services/debug.service';
import { LoggingService } from 'services/logging.service';
import { DocWatcher } from 'services/database-wrapper';
import { navigationStackChangedEvent, PubSubService, startWorkoutFromNavEvent, workoutStartableStateChangedEvent } from 'services/pubsub.service';
import {
  decideShowBottomNav,
  decideShowSideMenu,
  isMenuItemEnabled,
  isMenuItemNotificationDotVisible,
  isMenuItemVisible,
  PageInterface,
} from 'services/app-navigation-menu';

/**
 * The few things the menus need that belong to `AppComponent` and cannot move
 * with them. `AppComponent` passes this in when it initializes the menus.
 */
export interface AppNavigationMenuHost {
  /** Training and Nutrition stay disabled until onboarding is done and the user has paid. */
  isFullAppUnlocked(): boolean;
  isWelcomePhaseDone(): boolean;
  /**
   * Read as a function on purpose, NOT as a value. The boot doc watcher does not
   * exist yet when the menus are initialized — the boot sequence creates it later —
   * so a captured value would be `null` for the whole life of the app.
   */
  getDocWatcher(): DocWatcher;
  /** The root component's own change detector; the menus are drawn by its template. */
  markForCheck(): void;
}

/**
 * The side menu, the phone bottom bar and the list of pages they lead to (fourth
 * slice of deferred finding df-7b66acbeb341).
 *
 * Lifted out of `AppComponent` with no behavior change. The root template
 * (`app.html`) binds to this service directly. The decisions — when each menu
 * shows, which entries are visible or enabled — live in `app-navigation-menu.ts`,
 * which has no Angular in it and is unit tested.
 *
 * The two "Home" entries need OPPOSITE callbacks, and that is pinned by
 * `app/home-menu-navigation.test.ts`, which reads this file as text. See
 * `design-docs/navigation.md`, "Side-menu items must not navigate from their callback".
 */
@Injectable({ providedIn: 'root' })
export class AppNavigationMenuService {
  pages: PageInterface[] = [];
  bottomNavItems: BottomNavItem[] = [];
  canStartWorkout = false;

  private host: AppNavigationMenuHost;

  constructor(
    private navCtrl: NavController,
    private menuController: MenuController,
    private overlayStack: OverlayStackService,
    private activityMenuService: ActivityMenuService,
    private pubSub: PubSubService,
    private debug: DebugService,
    private logging: LoggingService,
  ) { }

  /** Build both menus and subscribe to the events that change them. Called once, from `AppComponent`'s constructor. */
  initialize(host: AppNavigationMenuHost): void {
    this.host = host;

    this.pages = [
      // NOTE: this callback must NOT navigate — openPageAsync navigates to pageRef itself
      // right after running it. See `design-docs/navigation.md`.
      { title: LL.navigation.home(), callback: () => { this.snapOpenHomePageToToday(); }, pageRef: IHomePage, icon: 'home' },
      { title: LL.navigation.training(), pageRef: ITrainingPage, icon: 'exercise', isEnabled: () => this.host.isFullAppUnlocked() },
      { title: LL.navigation.nutrition(), pageRef: INutritionPage, icon: 'dining', isEnabled: () => this.host.isFullAppUnlocked(), addSeparatorAfter: true },

      { title: LL.navigation.settings(), pageRef: ISettingsPage, icon: 'settings' },
      { title: '', pageRef: IAdminImpersonatedUserPage, icon: 'eye', isVisible: () => false /* profile is added manually */ },
    ];

    // initialize bottom nav items
    this.bottomNavItems = [
      { icon: 'home', label: LL.navigation.home(), pageRef: IHomePage, callback: () => this.resetToToday() },
      { icon: 'exercise', label: LL.navigation.training(), pageRef: ITrainingPage, isEnabled: () => this.host.isFullAppUnlocked() },
      { icon: 'dining', label: LL.navigation.nutrition(), pageRef: INutritionPage, isEnabled: () => this.host.isFullAppUnlocked() },
      { icon: 'settings', label: LL.navigation.settings(), pageRef: ISettingsPage }
    ];

    // subscribe to workout startable state changes from training page
    this.pubSub.subscribe(workoutStartableStateChangedEvent, (payload) => {
      this.canStartWorkout = payload.canStart;
      this.host.markForCheck();
    });

    // subscribe to navigation changes to re-evaluate menu visibility
    this.pubSub.subscribe(navigationStackChangedEvent, () => {
      this.host.markForCheck();
    });
  }

  isNotificationDotVisibleForAnyPage() {
    for (const page of this.pages) {
      if (this.isNotificationDotVisible(page)) {
        return true;
      }
    }

    return false;
  }

  isNotificationDotVisible(page: PageInterface): boolean {
    return isMenuItemNotificationDotVisible(page);
  }

  async openPageAsync(page: PageInterface) {
    /* Have to close the menu first, because it causes a pop on the nav stack.
    That pop can interfere with eventual opening of other pages in callbacks below
    E.g. Community page can't open if the menu back intercepts it. */
    this.menuClose();

    if (page.callback) {
      page.callback();
    }

    // if this page is just external link - special handling
    if (page.url) {
      window.open(page.url, page.windowName || '_self');
    }

    if (page.pageRef) {
      try {
        await this.navCtrl.setRootAsync(page.pageRef, {});
      } catch (err) {
        // the person picked a menu item and nothing opened. A page that deliberately refuses
        // to open never gets here (cs-nav answers false rather than throwing), so this is a
        // real failure — tell the person and report it, as cs-nav does for a page that crashes
        // on entry (#5191)
        this.logging.trackAbnormalException(err, LL.errors.userFacing.couldNotOpenPage());
      }
    }

    void this.menuController.close();
  }

  isVisible(page: PageInterface): boolean {
    return isMenuItemVisible(page);
  }

  isEnabled(page: PageInterface): boolean {
    return isMenuItemEnabled(page);
  }

  isSettingsPage(page: PageInterface): boolean {
    return page.pageRef && getPageName(page.pageRef) === 'SettingsPage';
  }

  async openSettingsAsync() {
    this.menuClose();
    await this.navCtrl.setRootAsync(ISettingsPage, {});
    void this.menuController.close();
  }

  async openAdminImpersonatedUserAsync() {
    this.menuClose();
    await this.navCtrl.setRootAsync(IAdminImpersonatedUserPage, {});
    void this.menuController.close();
  }

  isPageActive(page: PageInterface): boolean {
    if (!page.pageRef) return false;
    const activePage = this.getActivePageName();
    return activePage !== undefined && getPageName(page.pageRef) === activePage;
  }

  isSettingsPageActive(): boolean {
    return this.getActivePageName() === 'SettingsPage';
  }

  private getActivePageName() {
    return this.navCtrl.getActivePageName();
  }

  menuOpen() {
    this.overlayStack.setMenu(this.menuController);
  }
  menuClose() {
    this.overlayStack.unsetMenu();
  }

  shouldShowMenu() {
    return decideShowSideMenu({
      pageStack: this.navCtrl.getPageStack(),
      currentPage: this.getActivePageName(),
      getCurrentPageMode: () => this.navCtrl.getCurrentPageParams()?.mode,
      welcomePhaseDone: this.host.isWelcomePhaseDone(),
    });
  }

  shouldShowBottomNav() {
    return decideShowBottomNav({
      isRestoring: () => this.navCtrl.isRestoring(),
      isBelowCollapsedMenuBreakpoint: () => this.isBelowSplitPaneBreakpoint(),
      isOnRootPage: () => this.isOnRootPage(),
    });
  }

  isOnRootPage() {
    const currentPage = this.getActivePageName();
    return ROOT_PAGE_NAMES.includes(currentPage);
  }

  // Check if screen width is below collapsed menu breakpoint (used for bottom nav visibility)
  isBelowSplitPaneBreakpoint() {
    return document.documentElement.clientWidth < smallScreenMaxWidthBase + getSideMenuCollapsedWidth();
  }

  /** Snap an already-open Home page back to today, and report whether it did.
   *
   * Returns false when Home is not the active page — this deliberately does NOT open it.
   * Callers that need Home opened as well use resetToToday() below.
   *
   * This is the side menu's "Home" callback, and it must never navigate. See the
   * "Side-menu items must not navigate from their callback" note in
   * `design-docs/navigation.md` for why a second same-tick navigation is harmful. */
  private snapOpenHomePageToToday(): boolean {
    const activeView = this.navCtrl.getActive();
    if (activeView && getPageName(activeView.component.componentType) === "HomePage") {
      // reset to today and ensure Daily Log tab is selected
      (activeView.instance as HomePage).handleDayToday();
      return true;
    }
    return false;
  }

  /** Show today on Home, opening Home first when it is not already the active page.
   *
   * This is the BOTTOM BAR's "Home" callback, and unlike the side menu's it MUST navigate:
   * `cs-bottom-nav.handleItemClick` calls an item's callback INSTEAD of emitting `itemClick`,
   * so for an item that has one, this is the only code that runs for the tap. */
  resetToToday(): void {
    if (this.snapOpenHomePageToToday()) {
      return;
    }
    // navigate to home page with today's date and Daily Log tab
    void this.navCtrl.setRootAsync(IHomePage, {
      day: this.debug.getNowLocal().toISODate(),
      tab: HomeTab.DailyLog
    });
  }

  handleBottomNavItemClick(item: BottomNavItem) {
    if (item.pageRef) {
      void this.navCtrl.setRootAsync(item.pageRef, {});
    }
  }

  async handleBottomNavAddClick() {
    // show the activity menu anchored to the add button
    const addButton = document.querySelector('.bottom-nav__add-button') as HTMLElement;
    if (!addButton) return;

    await this.activityMenuService.showActivityMenu(addButton, this.host.getDocWatcher());
  }

  handleBottomNavStartWorkoutClick() {
    // publish event for training page to handle
    this.pubSub.publish(startWorkoutFromNavEvent, undefined);
  }
}
